// @ts-check

import TileLayer from 'ol/layer/Tile';
import { OSM, Vector as VectorSource, XYZ} from 'ol/source';
import View from 'ol/View';
import Map from 'ol/Map';
import { get as getProjection } from 'ol/proj';
import {Layer, Vector as VectorLayer} from 'ol/layer';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Select from 'ol/interaction/Select';
import {click} from 'ol/events/condition';
import Feature from 'ol/Feature'
import LayerGroup from 'ol/layer/Group';

class MainMap {

    /**
     * @param {String} id id
     * @param {String} fillColor color to fill shapes with
     */

    constructor(id, fillColor, selectedStrokeCollor) {

        this.defaultEPSG = 'EPSG:3857';
        this.defaultProj = getProjection(this.defaultEPSG);
        this.fillColor = fillColor;
        this.selectedStrokeCollor = selectedStrokeCollor;

        this.maxMapZoom = 18;
        this.minMapZoom = 8;
        this.maxTileZoomServer = 14; // how many zoom levels are on the server

        // how many lower level zoom tiles than current are preloaded
        // so if your current map zoom level is 16 and preload = 4, the map will load tiles for zooms: 16,14,13,12
        // this is done so you can change tile url at a greater zoom than this.maxTileZoomServer
        // eg. if your current map zoom is z = 16 and server only has tiles up to z = 14 
        // and you switch tile source url, it will fetch only z=16 tiles, whitch do not exist on server so the map won't show anything
        // but with preload, it will also load lower zooms and show the highest zoom tiles of those
        this.preload = this.maxMapZoom - this.maxTileZoomServer;

        this.sloExtent = [1488656, 5682638, 1847680, 5924636]

        this.id = id;

        this.layers = this.initLayers();

        this.wetlandFeatures = [];

        this.map = new Map({

            target: this.id,

            layers: this.layers,
            view: new View({
                center: [1590843, 5812674],
                projection: this.defaultEPSG,
                zoom: 12,
                minZoom: this.minMapZoom,
                maxZoom: this.maxMapZoom,

            }),

        });

        this.setBackgroundLayer('OSM');

        this.initSelectInteraction();

    }

    /**
     * Initializes layers
     * 
     * @returns {Array<Layer|LayerGroup>} all the layers/layergorups shown on the map
     */
    initLayers() {

        this.osm = new TileLayer({
            source: new OSM(),
        });

        this.google = new TileLayer({
            source: new XYZ({
              url: "http://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            })
        }), 

        this.tile = new TileLayer({
            preload: this.preload,
            source: new XYZ({
              url:  '',
            })
        }),

        this.tile.setExtent(this.sloExtent);

        this.wetlandsStyle = new Style({
            fill: new Fill({
                color: this.fillColor
            }),

            stroke: new Stroke({
                color: 'black',
                width: 1
            })
        })

        this.wetlands = new VectorLayer({
            source: new VectorSource({
            }),
            style: this.wetlandsStyle,
        })

        this.wetlandsFilteredStyle = this.wetlandsStyle.clone();

        this.wetlandsFilteredStyle.setFill(new Fill({
            color: 'rgba(1,33,33,0.5)',
        }))

        this.wetlandsFiltered = new VectorLayer({
            source: new VectorSource({
            }),
            style: this.wetlandsFilteredStyle,
        })

        this.wetlandsGroup = new LayerGroup({
            layers : [
                this.wetlands,
                this.wetlandsFiltered
            ]
        })

        this.backgroundLayers = [
            {
                name : 'OSM',
                layer : this.osm,
            },
            {
                name : 'Google',
                layer : this.google,
            },
        ]

        return [
            this.osm,
            this.google,
            this.tile,
            this.wetlandsGroup,
        ];
    }

    /**
     * Initializes the select feature interaction
     * 
     * @returns {Select} the select interaction
     */
    initSelectInteraction() 
    {

        this.selectedStyle = new Style({
            fill: new Fill({}),
            stroke: new Stroke({
                color : this.selectedStrokeCollor,
                width: 2,
            })
        });

        this.selectInteraction = new Select({
            layers: [this.wetlands, this.wetlandsFiltered],
            style: this.selectedStyle,
            condition: click,
        });


        this.map.addInteraction(this.selectInteraction);

        return this.selectInteraction;
    }

    /**
     * Adds features to wetlands layer source
     * 
     * @param {Array<Feature>} features 
     */
    addWetlandFeatures(features) {
    
        this.wetlands.getSource().addFeatures(features);
        this.wetlandFeatures = features;
    }
        

    /**
     * Refreshes tiles layer with tiles from date/polarization
     * 
     * @param {String} tilesUrl fuull url to tile server ({z}/{x}/{y})
     */
    refreshTileSource(tilesUrl)
    {
        this.tile.getSource().setUrl(tilesUrl);
    }

    /**
     * Sets the background layer
     * 
     * @param {String} name Layer name 
     */
    setBackgroundLayer(name) 
    {
        this.backgroundLayers.forEach((obj) => {

            if (obj.name == name)
                obj.layer.setVisible(true);

            else
                obj.layer.setVisible(false);
        } );
    }

    /**
     * 
     * Sets feature as selected feature. Clears previous selection.
     * 
     * @param {Feature} feature 
     */
    setSelectedFeature(feature)
    {
        this.selectInteraction.getFeatures().clear();

        if(feature)
            this.selectInteraction.getFeatures().push(feature);
    }

    /**
     * Returns true if selectInteraction has a selected feature
     * 
     * @returns {Boolean} true if there is a selected feature
     */
    hasSelectedFeature()
    {
        return this.selectInteraction.getFeatures().getLength() > 0;
    }

    /**
     * Returns currently selected feature. Returns null if no feature selected.
     * 
     * @returns {Feature} selected feature or null
     */
    getSelectedFeature()
    {
        if(this.hasSelectedFeature())
            return this.selectInteraction.getFeatures().getArray()[0];

        return null;
    }

    /**
     * Sets fill color of the selected feature
     * 
     * @param {import('ol/color').Color | import('ol/colorlike').ColorLike} color 
     */
    setSelectedStyleFillColor(color)
    {
        this.selectedStyle.getFill().setColor(color);
    }

    /**
     * Returns all featrues form wetlands both filtered and unfiltered
     * 
     * @returns {Array<Feature>}
     */
    getWetlandFeatures() 
    {
        return this.wetlandFeatures;
    }
    
    /**
     * 
     * Puts features in wetlands layer and the rest of wetland features into wetlandsFiltered layer
     * Also sets proper select style
     * 
     * @param {Array<Feature>} features 
     */
     filterWetlandFeatureStyles(features) 
     {

        let filtered = []

        this.wetlandFeatures.forEach(wetlandFeature => { 

            if(!features.includes(wetlandFeature))
                filtered.push(wetlandFeature);

        });

        this._setLayerFeatures(this.wetlands, features);
        this._setLayerFeatures(this.wetlandsFiltered, filtered);

        if(this.hasSelectedFeature())
            if(filtered.includes(this.getSelectedFeature()))
                this.setSelectedStyleFillColor(this.wetlandsFilteredStyle.getFill().getColor());
            else
                this.setSelectedStyleFillColor(this.wetlandsStyle.getFill().getColor());

     }

     /**
      * Removes all features from vector layer and adds new features
      * 
      * @param {VectorLayer} layer 
      * @param {Array<Feature>} features 
      */
     _setLayerFeatures(layer, features)
     {
        layer.getSource().clear();
        layer.getSource().addFeatures(features);
 
     }


}
export default MainMap;
